import { prepareUrl } from 'src/utils/routerHelpers';
import { API_URL } from './apiDictionary';
import { getClient } from './client';

/**
 * Request body for `PUT /reader/tasks/:parentTaskId/:taskId/calculate/rano`.
 * Carries the reader-supplied leptomeningeal answer and, only when the form
 * sends one, the reader's `overrideResponse`; everything else is derived from
 * the persisted task/visit measurements.
 *
 * NOTE: BE `RanoRequestDto` declares `overrideResponse` as an enum
 * (`CONFIRMED_PD`, `SD`, `PSEUDO_PROGRESSION`, …). It stays `string` here on
 * purpose: D7 forwards the form's value and leaves validation to BE, so a value
 * BE adds later is not silently dropped. dynamika-mcp `types.gen.ts` is stale
 * on this DTO until its client is regenerated.
 */
export type CalculateRanoBody = {
  leptomeningealDisease: boolean;
  overrideResponse?: string;
};

export type CalculateRanoArgs = {
  clinicalTrialId: string;
  parentTaskId: string;
  taskId: string;
  body: CalculateRanoBody;
};

/**
 * Per BE contract: returns the calculated response, per-visit history with
 * full lesion snapshots (`responses[].lesions[]`), and scalar SPPD totals.
 * The shape is opaque to the platform — it is forwarded verbatim to the
 * scoring form via the `updateForm` event.
 */
export type CalculateRanoResult = unknown;

export const calculateRanoScoring = (args: CalculateRanoArgs) => {
  const api = getClient(API_URL.readerParentTaskCalculateRano);

  return api.put<CalculateRanoResult>(
    prepareUrl(API_URL.readerParentTaskCalculateRano, [
      args.parentTaskId,
      args.taskId,
    ]),
    args.body,
    {
      headers: {
        'x-dynamika-clinical-trial-id': args.clinicalTrialId,
      },
    }
  );
};
